import React, { useEffect, useState } from 'react';
import { Card, Form, Input, InputNumber, Button, Popconfirm, message, List, Upload, Switch } from 'antd';
import { DeleteOutlined, UploadOutlined, CalculatorOutlined } from '@ant-design/icons';
import { getValveFilsUrls, deleteValveFile, uploadValveFiles } from '../../../Utils/exploitations/valves';
import { calculateTotalSurface } from '../../../Utils/exploitations/exploitations';
import './disabled_input.css';



const ValveDataForm = ({ initialValues, onSubmit, form, brutSurface, setBrutSurface, isAutoCalculate, setIsAutoCalculate }) => {


  const calculateFlow = (changedvalues, allvalues) => {
    if (changedvalues.valve_superficie_brut) {
      setBrutSurface(form.getFieldValue('valve_superficie_brut'));
    }
    const { valve_superficie_net, pluviometry } = form.getFieldsValue(['valve_superficie_net', 'pluviometry']);
    if (valve_superficie_net && pluviometry) {
      const flow = valve_superficie_net * pluviometry;
      form.setFieldsValue({ flow: parseFloat(flow.toFixed(2)) });
    } else {
      form.setFieldsValue({ flow: null });
    }
    form.validateFields(['valve_superficie_net', 'pluviometry', 'flow', 'valve_superficie_brut']);
  };

  return (
    <Form
      layout="vertical"
      form={form}
      onFinish={onSubmit}
      initialValues={initialValues}
      onValuesChange={calculateFlow}
    >
      <Form.Item
        name="valve_name"
        label="Nom"
        rules={[{ required: true, message: 'Veuillez entrer un nom' }]}
      >
        <Input />
      </Form.Item>
      <Form.Item
        label={
          <span>
            Superficie brute (ha)
            <Switch
              checkedChildren={<p>auto</p> }
              unCheckedChildren={<p>manuelle</p>}
              checked={isAutoCalculate}
              onChange={(checked) => setIsAutoCalculate(checked)}
              style={{ marginLeft: '10px' }}
            />
          </span>
        }
      >
        <Form.Item
          name="valve_superficie_brut"
          noStyle
        >
          <InputNumber
            min={0}
            style={{ width: '100%' }}
            disabled={isAutoCalculate}
          />
        </Form.Item>
      </Form.Item>
      <Form.Item
        name="valve_superficie_net"
        label="Superficie nette (ha)"
        rules={[
          { required: true, message: 'Veuillez entrer la superficie nette' },
          {
            validator: (_, value) =>
              value <= brutSurface
                ? Promise.resolve()
                : Promise.reject('La superficie nette doit être inférieure ou égale à la superficie brute')
          }
        ]}
      >
        <InputNumber min={0} max={brutSurface} style={{ width: '100%' }} />
      </Form.Item>
      <Form.Item
        name="culture"
        label="Culture"
      >
        <Input />
      </Form.Item>
      <Form.Item
        name="variety"
        label="Variété"
      >
        <Input />
      </Form.Item>
      <Form.Item
        name="plantation_density"
        label="Densité de plantation"
      >
        <Input />
      </Form.Item>
      <Form.Item
        name="line"
        label="Ligne"
      >
        <Input />
      </Form.Item>
      <Form.Item
        name="foot"
        label="Pied"
      >
        <Input />
      </Form.Item>
      <Form.Item
        name="pluviometry"
        label="Pluviométrie (m³/h/Ha)"
        rules={[{ required: true, message: 'Veuillez entrer la pluviométrie' }]}
      >
        <Input />
      </Form.Item>
      <Form.Item
        name="flow"
        label="Débit"
      >
        <InputNumber min={0} style={{ width: '100%' }} disabled className='custom-disabled-input' />
      </Form.Item>
      <Form.Item>
        <Button type="primary" htmlType="submit">
          Mettre à jour les données
        </Button>
      </Form.Item>
    </Form>
  );

};


const FileManagementForm = ({ valveId, kmlFiles, setKmlFiles, calculateBrutSurface, isAutoCalculate  }) => {
  const fetchUpdatedFiles = async () => {
    try {
      const response = await getValveFilsUrls(valveId);
      const updatedFiles = response.file_urls.map(url => ({ url, name: url.split('/').pop() }));
      setKmlFiles(updatedFiles);
    } catch (error) {
      console.error('Error fetching updated files:', error);
      message.error('Erreur lors de la récupération des fichiers mis à jour');
    }
  };

  const handleFileUpload = async (file) => {
    try {
      const formData = new FormData();
      formData.append('files', file);

      await uploadValveFiles(valveId, formData);
      message.success('Fichier KML ajouté avec succès');


      // Fetch updated file list and recalculate brut surface if auto-calculate is on
      await fetchUpdatedFiles();
      if (isAutoCalculate) {
        calculateBrutSurface([...kmlFiles, file]);
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      message.error('Erreur lors de l\'ajout du fichier KML: ' + (error.message || 'Unknown error'));
    }
    return false; // Prevent default upload behavior
  };

  const handleFileDelete = async (fileName) => {
    try {
      await deleteValveFile(valveId, fileName);
      message.success('Fichier KML supprimé avec succès');

      // Fetch updated file list and recalculate brut surface if auto-calculate is on
      await fetchUpdatedFiles();
      if (isAutoCalculate) {
        const updatedFiles = kmlFiles.filter(file => file.name !== fileName);
        calculateBrutSurface(updatedFiles);
      }
    } catch (error) {
      console.error('Error deleting file:', error);
      message.error('Erreur lors de la suppression du fichier KML: ' + (error.message || 'Unknown error'));
    }
  };

  return (
    <>
      <List
        dataSource={kmlFiles}
        renderItem={(file) => (
          <List.Item
            actions={[
              <Popconfirm
                title="Êtes-vous sûr de vouloir supprimer ce fichier ?"
                onConfirm={() => handleFileDelete(file.name)}
                okText="Oui"
                cancelText="Non"
              >
                <DeleteOutlined />
              </Popconfirm>
            ]}
          >
            <List.Item.Meta
              title={file.name}
            />
          </List.Item>
        )}
      />

      <Upload
        accept=".kml"
        multiple={false}
        fileList={[]}
        beforeUpload={handleFileUpload}
      >
        <Button icon={<UploadOutlined />}>Ajouter un fichier KML</Button>
      </Upload>
    </>
  );
};

const ValveEditForm = ({ initialValues, onSubmit, onDelete }) => {
  const [form] = Form.useForm();
  const [kmlFiles, setKmlFiles] = useState([]);
  const [brutSurface, setBrutSurface] = useState(initialValues.valve_superficie_brut || 0);
  const [isAutoCalculate, setIsAutoCalculate] = useState(true);


  useEffect(() => {
    form.setFieldsValue(initialValues);
    if (initialValues.valve_id) {
      fetchExistingFiles(initialValues.valve_id);
    }
    setBrutSurface(initialValues.valve_superficie_brut || 0);
  }, [form, initialValues]);

  const fetchExistingFiles = async (valveId) => {
    try {
      const response = await getValveFilsUrls(valveId);
      setKmlFiles(response.file_urls.map(url => ({ url, name: url.split('/').pop() })));
    } catch (error) {
      console.error('Error fetching existing files:', error);
      message.error('Erreur lors de la récupération des fichiers existants');
    }
  };


  const calculateBrutSurface = async (files) => {
    if (files.length > 0 && isAutoCalculate) {
      try {
        const fileContents = await Promise.all(
          files.map(async (file) => {
            if (file.name) {
              const response = await fetch(process.env.REACT_APP_KML_PATH + file.name);
              return await response;
            }
            return await file.originFileObj;
          })
        );


        const surface = await calculateTotalSurface(fileContents);
        setBrutSurface(surface);
        form.setFieldsValue({ valve_superficie_brut: surface });
      } catch (error) {
        console.error('Error calculating surface:', error);
        message.error('Erreur lors du calcul de la surface brute');
      }
    } else if (!isAutoCalculate) {
      // If auto-calculate is off, keep the current form value
      setBrutSurface(form.getFieldValue('valve_superficie_brut') || 0);
    } else {
      // If no files and auto-calculate is on, set to 0
      setBrutSurface(0);
      form.setFieldsValue({ valve_superficie_brut: 0 });
    }
  };


  useEffect(() => {
    if (isAutoCalculate) {
      calculateBrutSurface(kmlFiles);
    }
  }, [isAutoCalculate, kmlFiles]);

  useEffect(() => {
    calculateBrutSurface(kmlFiles);
  }, [kmlFiles]);

  const handleSubmit = async (values) => {
    try {
      await onSubmit({
        ...values,
        valve_id: initialValues.valve_id,
      });
      message.success('Données de la vanne mises à jour avec succès');
    } catch (error) {
      console.error('Error updating valve data:', error);
      message.error('Erreur lors de la mise à jour des données de la vanne');
    }
  };

  return (
    <Card
      title={`Modifier Vanne ${initialValues.number}`}
      extra={
        <Popconfirm
          title="Êtes-vous sûr de vouloir supprimer cette vanne ?"
          onConfirm={() => onDelete(initialValues.valve_id)}
          okText="Oui"
          cancelText="Non"
        >
          <DeleteOutlined className='text-red-500 text-2xl hover:bg-red-100 p-2 rounded-full' />
        </Popconfirm>
      }
      className='bg-indigo-50 w-80 mb-4'
    >
      <ValveDataForm
        initialValues={initialValues}
        onSubmit={handleSubmit}
        form={form}
        brutSurface={brutSurface}
        setBrutSurface={setBrutSurface}
        isAutoCalculate={isAutoCalculate}
        setIsAutoCalculate={setIsAutoCalculate}
      />
      <FileManagementForm
        valveId={initialValues.valve_id}
        kmlFiles={kmlFiles}
        setKmlFiles={setKmlFiles}
        calculateBrutSurface={calculateBrutSurface}
        isAutoCalculate={isAutoCalculate}
      />
    </Card>
  );
};

export default ValveEditForm;